<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, toRefs } from "vue";
import { showToast } from "vant";
import { getVerifyCode, loginByVerify } from "../request/apis/login";
import SvgIcon from "./SvgIcon.vue";
import { LoginProps } from "../types/login";
import { globalLogin } from "../utils/auth";

const showLogin = ref(false);
const props = defineProps<LoginProps>();
const { title, subTitle, confirmText, cancelText, showCancel, tips } = toRefs(props);
const { onConfirm, onCancel } = props;

const phoneNumber = ref("");
const authCode = ref("");

let codeText = ref("获取验证码");
const isSend = ref(false);
let remainingTime = 0;
let timer = <any>0;
let totalTime = 60;
const sendLoading = ref(false);
const captchaAppId = import.meta.env.VITE_APP_CAPTCHA_APP_ID;
const CAPTCHA_SCRIPT_SRC = "https://turing.captcha.qcloud.com/TJCaptcha.js";
let captchaScriptPromise: Promise<void> | null = null;

const verifyPhone = computed(() => {
  let regex = /^1[23456789]\d{9}$/;
  return regex.test(phoneNumber.value!);
});
const verifyCode = computed(() => {
  let regex = /^\d{4}$/;
  return regex.test(authCode.value!);
});

const loadTencentCaptchaScript = () => {
  if (window.TencentCaptcha) return Promise.resolve();
  if (captchaScriptPromise) return captchaScriptPromise;

  captchaScriptPromise = new Promise<void>((resolve, reject) => {
    const existedScript = document.querySelector(`script[src="${CAPTCHA_SCRIPT_SRC}"]`) as HTMLScriptElement | null;
    if (existedScript) {
      existedScript.addEventListener("load", () => resolve(), { once: true });
      existedScript.addEventListener("error", () => reject(new Error("CAPTCHA_SCRIPT_LOAD_FAIL")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = CAPTCHA_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("CAPTCHA_SCRIPT_LOAD_FAIL"));
    document.body.appendChild(script);
  }).catch((error) => {
    captchaScriptPromise = null;
    throw error;
  });

  return captchaScriptPromise;
};

const runTencentCaptcha = async () => {
  if (!captchaAppId) {
    throw new Error("CAPTCHA_APP_ID_MISSING");
  }

  await loadTencentCaptchaScript();

  return new Promise<{ ticket: string; randstr: string }>((resolve, reject) => {
    if (!window.TencentCaptcha) {
      reject(new Error("CAPTCHA_NOT_READY"));
      return;
    }

    const captcha = new window.TencentCaptcha(String(captchaAppId), (res: any) => {
      if (res?.ret === 0 && res?.ticket && res?.randstr) {
        resolve({ ticket: res.ticket, randstr: res.randstr });
      } else {
        reject(new Error("CAPTCHA_CANCELLED"));
      }
    });

    captcha.show();
  });
};

const startCountDown = () => {
  isSend.value = true;
  remainingTime = 0;
  clearInterval(timer);
  timer = setInterval(() => {
    if (remainingTime < totalTime) {
      remainingTime++;
      codeText.value = totalTime - remainingTime + "s后重新获取";
    } else {
      isSend.value = false;
      clearInterval(timer);
      codeText.value = "重新发送";
      remainingTime = 0;
    }
  }, 1000);
};

const sendCode = async () => {
  if (phoneNumber.value! == "") {
    showToast("请输入手机号码");
    return false;
  }
  if (!verifyPhone.value) {
    showToast("手机号码格式不正确");
    return false;
  }
  if (isSend.value) return;
  sendLoading.value = true;
  try {
    const { ticket, randstr } = await runTencentCaptcha();
    await getVerifyCode({
      phone: phoneNumber.value.replace(/(\s*)/g, ""),
      "tencent_captcha.ticket": ticket,
      "tencent_captcha.rand_str": randstr,
      code_type: 1
    });
    showToast("发送验证码成功");
    startCountDown();
  } catch (error: any) {
    if (error?.message === "CAPTCHA_APP_ID_MISSING") {
      showToast("验证码配置缺失");
    } else if (error?.message !== "CAPTCHA_CANCELLED") {
      showToast("发送验证码失败");
    }
  } finally {
    sendLoading.value = false;
  }
};

const loginLoading = ref(false);

const onSubmit = () => {
  if (phoneNumber.value!.trim() == "") {
    showToast("请输入手机号码");
    return false;
  }
  if (!verifyPhone.value) {
    showToast("手机号码格式不正确");
    return false;
  }
  if (authCode.value!.trim() == "") {
    showToast("请输入验证码");
    return false;
  }
  if (!verifyCode.value) {
    showToast("验证码格式不正确");
    return false;
  }
  loginLoading.value = true;
  if (onConfirm) {
    onConfirm({ phone: phoneNumber.value, code: authCode.value })
      .then(() => {
        loginLoading.value = false;
        showLogin.value = false;
      })
      .catch(() => {
        loginLoading.value = false;
      });
  } else {
    loginByVerify({ phone: phoneNumber.value, code: authCode.value, login_type: 1 })
      .then(({ data }) => {
        if (data.code === 200) {
          if (!data?.data?.is_login) {
            showToast("该用户还未完成注册！");
          } else {
            // 使用全局登录状态管理
            globalLogin(data.data.token || "", data.data);
            showLogin.value = false;
            props.onSuccess?.();
          }
        } else {
          showToast(data.message);
        }
      })
      .finally(() => {
        loginLoading.value = false;
      });
  }
};
const close = () => {
  showLogin.value = false;
  onCancel?.();
};
onMounted(() => {
  showLogin.value = true;
  if (props.phone) {
    phoneNumber.value = props.phone;
  }
});

onUnmounted(() => {
  clearInterval(timer);
});
</script>
<template>
  <div>
    <van-dialog lazy-render v-model:show="showLogin" :showCancelButton="false" :show-confirm-button="false" width="8.5333rem">
      <div class="w-full relative pt-22 pb-14">
        <p class="color-text text-16 fw-500 lh-22 text-center mb-12">{{ title }}</p>
        <p v-if="subTitle" class="text-13 lh-18 color-text-tertiary text-center mt-8">{{ subTitle }}</p>
        <div class="absolute top-12 right-12">
          <van-icon name="cross" size="16px" color="#90959E" @click="close" />
        </div>
        <div class="mt-8">
          <van-config-provider :theme-vars="{ fieldPlaceholderTextColor: '#90959E' }">
            <van-form @submit="onSubmit" label-width="48px">
              <van-cell-group :border="false">
                <div class="px-4">
                  <van-field class="important-text-16" v-model="phoneNumber" placeholder="输入手机号码" type="tel" :maxlength="11" :rules="[{ required: true, message: '请输入手机号码' }]">
                    <template #left-icon>
                      <SvgIcon name="phone" width=".5333rem" height="0.5333rem" :color="phoneNumber ? '#1F1E1E' : '#90959E'" />
                    </template>
                  </van-field>
                  <van-field class="important-text-16" border v-model="authCode" center clearable placeholder="请输入验证码" type="digit" :maxlength="4">
                    <template #button>
                      <van-button size="small" plain class="important-border-none important-text-14" type="primary" :disabled="isSend || phoneNumber.length !== 11" :loading="sendLoading" @click="sendCode">{{ codeText }}</van-button>
                    </template>
                    <template #left-icon>
                      <SvgIcon class="mt-6" name="verify" width=".5333rem" height="0.5333rem" :color="authCode ? '#1F1E1E' : '#90959E'" />
                    </template>
                  </van-field>
                </div>
              </van-cell-group>
              <div class="w-full flex gap-20 px-16 pt-16 pb-10 justify-center">
                <van-button v-if="showCancel" round block type="primary" class="btn-main cancel important-h-36 max-w-106" @click="close">
                  {{ cancelText }}
                </van-button>
                <van-button round block class="btn-main important-h-36" :class="{ 'max-w-106': showCancel }" type="primary" :loading="loginLoading" native-type="submit">
                  {{ confirmText }}
                </van-button>
              </div>
            </van-form>
          </van-config-provider>
        </div>
        <p v-if="tips" class="text-12 lh-17 color-text-tertiary text-center mb-10">{{ tips }}</p>
      </div>
    </van-dialog>
  </div>
</template>
<style lang="scss" scoped></style>
